import React from 'react';
import moment from 'moment';
import cx from 'classnames';
import {IconButton, Box} from '@material-ui/core';
import DatePicker from './DatePicker';

import {DatePickerProps} from './types';

import {useStyles} from './RangePicker.styles';

export const WeekRangePicker = ({
    onChange,
    onCalendarClick,
    isPreselection,
    selectedDate,
    startDate,
    endDate,
    ...props
}: DatePickerProps) => {
    const classes = useStyles();

    const renderWrappedWeekDay = (date, selectedDate, dayInCurrentMonth) => {
        if (moment(startDate).isSame(endDate, 'day')) {
            onChange(startDate);
        }

        const mDate = moment(date);
        // Disallow selecting current week
        const isFuture = mDate.isAfter(
            moment()
                .startOf('week')
                .subtract(1, 'days'),
            'day'
        );
        const isFirstDay = mDate.isSame(startDate, 'day');
        const isLastDay = mDate.isSame(endDate, 'day');
        const isDayBetween = mDate.isBetween(startDate, endDate);
        // Suppose that locale set up to start week from Monday
        const isMonday = mDate.weekday() === 0;
        const isSunday = mDate.weekday() === 6;
        const isLongerThanWeek = moment(endDate).diff(startDate, 'day') > 6;
        const isFirstSunday = isSunday && isLongerThanWeek && mDate.diff(startDate, 'day') === 6;
        const isLastSunday = isSunday && isLongerThanWeek && isLastDay;
        const isMiddleSunday = isSunday && !isFirstSunday && !isLastSunday && !isLastDay;

        const isInterval = !moment(startDate).isSame(endDate, 'day');
        const isSelectedDay = mDate.isSame(selectedDate, 'day');

        const baseDayClassName = cx(classes.day, {
            [classes.nonCurrentMonthDay]: !dayInCurrentMonth,
            [classes.disabledFutureDay]: isFuture && dayInCurrentMonth,
            [classes.inactiveEmphasis]: isMonday && !isDayBetween && !isFirstDay && !isFuture,
            [classes.inactiveFutureEmphasis]: isMonday && !isDayBetween && !isFirstDay && isFuture
        });

        // Styles before first calendar click
        const preselectedWrapperClassName = cx({
            [classes.preselectedActiveHighlight]: !isInterval && isSelectedDay,
            [classes.preselectedHighlight]: isDayBetween && !isLastDay,
            [classes.preselectedFirstHighlight]: isInterval && isFirstDay,
            [classes.preselectedLastHighlight]: isInterval && isLastDay
        });

        const preselectedDayClassName = cx(classes.day, {
            [classes.preselectedEmphasis]: isInterval && (isFirstDay || isLastDay)
        });

        // Styles after first calendar click
        const wrapperClassName = cx({
            [classes.highlight]: isDayBetween && !isLastDay && !isSunday,
            [classes.firstHighlight]: isFirstDay || (isDayBetween && isMonday),
            [classes.lastWeekDayHighlight]: isLastDay && !isLastSunday,
            [classes.firstSundayHighlight]: isFirstSunday && isDayBetween,
            [classes.middleSundayHighlight]: isMiddleSunday && isDayBetween,
            [classes.lastSundayHighlight]: isLastSunday
        });

        const dayClassName = cx(classes.day, {
            [classes.emphasis]: (isInterval && isDayBetween && isMonday) || isFirstDay
        });

        const onPreviousMonthMondayClick = () => {
            if (!dayInCurrentMonth) {
                onCalendarClick(mDate);
            }
        };

        return (
            <Box className={isPreselection ? preselectedWrapperClassName : wrapperClassName}>
                <IconButton
                    onClick={onPreviousMonthMondayClick}
                    className={`${
                        isPreselection ? preselectedDayClassName : dayClassName
                    } ${baseDayClassName}`}>
                    <span>{mDate.format('D')}</span>
                </IconButton>
            </Box>
        );
    };

    // Disallow selecting current week
    const disableDates = date => {
        return date.isBetween(
            moment()
                .startOf('week')
                .subtract(1, 'days'),
            moment().endOf('week'),
            'day'
        );
    };

    return (
        <DatePicker
            onChange={onChange}
            renderDay={renderWrappedWeekDay}
            shouldDisableDate={disableDates}
            {...props}
        />
    );
};

export default WeekRangePicker;
